/**
 * 隐私政策页（决策 4：海外标准一期上线；M5 待办「隐私政策页 + Cookie 告知」）。
 * 静态双语页（不进后台 blocks——法务文本需要版本固定，改版走代码 + consentVersion 升版）。
 * 结构对齐原型 story 布局；内容基于工程实况书写（信使剧本 05 / 埋点 09 / 附件存储决策 2 / 日志脱敏决策 9），
 * 交品牌/法务核对（M5 清单）。
 */
import { useLocale } from '../lib/locale';
import { SiteHeader } from '../components/SiteHeader';

/** 双语条款（zh / en 成对；en 为 Claude 审校稿） */
const SECTIONS: { h: string; hEn: string; p: string; pEn: string }[] = [
  {
    h: '我们收集什么',
    hEn: 'What we collect',
    p: '通过联络站（「我是创作者」「我是合作伙伴」）提交信息时，我们会收集你在对话中主动填写的内容：称呼、电子邮箱、所在城市（选填）、创作方向或合作类型、留言，以及你主动上传的附件（如作品集、方案文件，单个不超过 50MB）。除此之外，我们不通过其他渠道收集你的个人信息。',
    pEn: 'When you submit information through the Contact Station ("I’m a creator" / "I’m a partner"), we collect what you actively provide in the conversation: your name, email address, city (optional), creative focus or collaboration type, your message, and any files you upload (such as a portfolio or brief, up to 50MB each). We do not collect personal information through any other channel.',
  },
  {
    h: '我们如何使用',
    hEn: 'How we use it',
    p: '你提交的信息只用于回复你的联络与评估合作可能。提交内容保存在我们的后台，由授权运营人员查看；不会用于自动化决策、不会对外出售、不会用于与本站无关的营销。一期不发送邮件通知——回复通过你留下的邮箱由人工发出。',
    pEn: 'What you submit is used only to reply to your message and evaluate a potential collaboration. Submissions are stored in our admin console and viewed by authorised staff only. They are not used for automated decision-making, not sold, and not used for marketing unrelated to this site. In phase one we do not send automated emails — replies are sent manually to the email address you leave.',
  },
  {
    h: '附件与存储',
    hEn: 'Attachments and storage',
    p: '你上传的附件通过加密直传存入我们的对象存储（阿里云 OSS，位于站点服务区域），仅授权人员可通过后台下载。上传后未随问卷一并提交的附件会在 24 小时后自动删除。',
    pEn: 'Files you upload are transferred over an encrypted connection to our object storage (Alibaba Cloud OSS, located in the region where this site is served) and can be downloaded only by authorised staff. An upload that is never submitted with the form is automatically deleted after 24 hours.',
  },
  {
    h: '访问统计',
    hEn: 'Analytics',
    p: '为了解页面使用情况，本站记录匿名化的页面事件（如页面浏览、板块展开），不包含你的姓名、邮箱、留言内容或任何可直接识别你身份的信息。你的 IP 地址经单向哈希处理后仅用于防滥用（限制频繁提交），不以明文存储。',
    pEn: 'To understand how pages are used, this site records anonymised page events (such as page views and section opens) that contain no name, email, message content, or directly identifying information. Your IP address is one-way hashed and used solely for abuse prevention (rate limiting); it is never stored in plain text.',
  },
  {
    h: 'Cookie 与本地存储',
    hEn: 'Cookies and local storage',
    p: '本站不使用跟踪 Cookie 或第三方广告 SDK。浏览器本地存储（localStorage）仅记住你的偏好设置：界面语言、浅色/深色主题、小镇首页的昼夜模式。清除浏览器数据即可重置这些偏好。',
    pEn: 'This site does not use tracking cookies or third-party advertising SDKs. Browser local storage remembers only your preferences: interface language, light/dark theme, and the day/night mode of the town homepage. Clearing your browser data resets them.',
  },
  {
    h: '保留与删除',
    hEn: 'Retention and deletion',
    p: '联络提交内容在合作评估期间保留；如需删除你的提交（含附件），请通过联络站留言并注明提交时使用的邮箱，我们将在核实后删除。我们会在本政策更新时在此页面公布并更新版本号。',
    pEn: 'Contact submissions are kept for as long as the collaboration assessment takes. To have your submission (including attachments) deleted, leave us a message via the Contact Station quoting the email address you used; we will delete it after verification. Updates to this policy will be published on this page with a new version number.',
  },
  {
    h: '联系我们',
    hEn: 'Contact us',
    p: '关于本政策或你个人信息处理的任何问题，请通过联络站（首页「联络站」）提交，或发送邮件至站点页脚公示的官方邮箱。',
    pEn: 'For any question about this policy or the processing of your personal information, reach us via the Contact Station (from the homepage) or the official email published on this site.',
  },
];

export function PrivacyPage() {
  const { locale } = useLocale();
  const zh = locale === 'zh-CN';
  return (
    <main className='page' id='main'>
      <SiteHeader />
      <article className='story'>
        <header className='page-intro'>
          <p className='eyebrow'>{zh ? '隐私说明' : 'Privacy Notice'}</p>
          <h1>{zh ? '隐私说明' : 'Privacy Notice'}</h1>
        </header>
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>
          {zh
            ? '版本 2026-09-18 · 本页内容以品牌/法务终审稿为准'
            : 'Version 2026-09-18 · Subject to the final brand/legal review'}
        </p>
        {SECTIONS.map((s) => (
          <section key={s.h}>
            <h2>{zh ? s.h : s.hEn}</h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{zh ? s.p : s.pEn}</p>
          </section>
        ))}
      </article>
    </main>
  );
}
